import React from 'react';
import i18n from '../i18n/i18n';

import {
  IconAlertTriangle,
  IconCircleCheck,
  IconClock,
  IconXboxX,
} from '@tabler/icons-react'; // o da dove provengono

import { BookingStatus, PaymentStatus } from '@interfaces/booking.interface';
import { HolidayStatus } from '@interfaces/holidays.interface';

export const mapRoomTypeName = (typeId: string): string => {
  const t = i18n.t;
  const [type, variant, shareable, sharedWith] = typeId.split('_');

  let name = '';

  switch (type) {
    case 'S':
      name = t('rooms.types.single');
      break;
    case 'D':
      name = t('rooms.types.double');
      break;
    case 'T':
      name = t('rooms.types.triple');
      break;
    case 'Q':
      name = t('rooms.types.quadruple');
      break;
    case 'DUS':
      name = t('rooms.types.doubleSingleUse');
      break;
    case 'M':
      name = t('rooms.types.doubleMatrimonial');
      break;
    case 'CAST':
      name = t('rooms.types.doubleBunk');
      break;
    case 'DIN':
      name = t('rooms.types.dinette');
      break;
    case 'CUC':
      name = t('rooms.types.berth');
      break;
    case 'P':
      return t('rooms.types.dormitory');
  }

  switch (variant) {
    case 'S':
      name += ` ${t('rooms.variants.superior')}`;
      break;
    case 'D':
      name += ` ${t('rooms.variants.deluxe')}`;
      break;
    case 'SB':
      name += ` ${t('rooms.variants.sharedBathroom')}`;
      break;
    case 'PB':
      name += ` ${t('rooms.variants.privateBathroom')}`;
      break;
  }

  if (shareable === 'S') {
    name += ` ${t('rooms.shared.shared')}`;
  }

  switch (sharedWith) {
    case 'M':
      name += ` ${t('rooms.shared.male')}`;
      break;
    case 'F':
      name += ` ${t('rooms.shared.female')}`;
      break;
  }

  return name;
};

export const compareRoomTypes = (a: string, b: string): boolean => {
  const [, , , sharedWithA] = a.split('_');
  const [, , , sharedWithB] = b.split('_');

  if (sharedWithA || sharedWithB) {
    // Ignore shared with
    return a.substring(0, -2) === b.substring(0, -2);
  } else {
    return a === b;
  }
};

export const mapRoomTypeInfo = (typeId: string): string | null => {
  const t = i18n.t;
  const [, , shareable] = typeId.split('_');

  let info = null;

  if (shareable === 'S') {
    info = t('rooms.shared.info');
  }

  return info;
};

export const mapBookingStatus = (
  status: BookingStatus,
): {
  label: string;
  tooltip: string;
  color: string;
  icon: React.ReactNode;
} => {
  const t = i18n.t;
  switch (status) {
    case 'confirmed':
      return {
        label: t('status.booking.confirmed.label'),
        tooltip: t('status.booking.confirmed.tooltip'),
        color: 'green',
        icon: React.createElement(IconCircleCheck),
      };
    case 'canceled':
      return {
        label: t('status.booking.canceled.label'),
        tooltip: t('status.booking.canceled.tooltip'),
        color: 'red',
        icon: React.createElement(IconXboxX),
      };
    case 'pending':
      return {
        label: t('status.booking.pending.label'),
        tooltip: t('status.booking.pending.tooltip'),
        color: 'yellow',
        icon: React.createElement(IconClock),
      };
    case 'suspended':
      return {
        label: t('status.booking.suspended.label'),
        tooltip: t('status.booking.suspended.tooltip'),
        color: 'red',
        icon: React.createElement(IconAlertTriangle),
      };
    default:
      return {
        label: t('status.booking.unavailable.label'),
        tooltip: t('status.booking.unavailable.tooltip'),
        color: 'grey',
        icon: React.createElement(IconClock),
      };
  }
};

export const mapHolidayVariationStatus = (
  status: HolidayStatus | null,
): { label: string; color: string; tooltip: string; icon: React.ReactNode } => {
  const t = i18n.t;
  if (!status) {
    return {
      label: t('status.holiday.unavailable.label'),
      tooltip: t('status.holiday.unavailable.tooltip'),
      color: 'grey',
      icon: React.createElement(IconClock),
    };
  }
  switch (status) {
    case 'confirmed':
    case 'lastPlaces':
    case 'onRequest':
    case 'soldOut':
    case 'promo':
    case 'lastMinute':
      return {
        label: t('status.holiday.confirmed.label'),
        tooltip: t('status.holiday.confirmed.tooltip'),
        color: 'green',
        icon: React.createElement(IconCircleCheck),
      };
    default:
      return {
        label: t('status.holiday.pending.label'),
        tooltip: t('status.holiday.pending.tooltip'),
        color: 'yellow',
        icon: React.createElement(IconClock),
      };
  }
};

export const mapPaymentStatus = (
  totalPaid: number,
  totalPrice: number,
  paymentStatus: PaymentStatus,
): { label: string; color: string; tooltip: string; icon: React.ReactNode } => {
  const t = i18n.t;

  if (totalPrice === 0) {
    return {
      label: t('status.payment.notRequired.label'),
      tooltip: t('status.payment.notRequired.tooltip'),
      color: 'green',
      icon: React.createElement(IconCircleCheck),
    };
  }

  switch (paymentStatus) {
    case 'paid':
      if (totalPaid >= totalPrice) {
        return {
          label: t('status.payment.paid.label'),
          tooltip: t('status.payment.paid.tooltip'),
          color: 'green',
          icon: React.createElement(IconCircleCheck),
        };
      } else {
        return {
          label: t('status.payment.pendingBalance.label'),
          tooltip: t('status.payment.pendingBalance.tooltip'),
          color: 'yellow',
          icon: React.createElement(IconClock),
        };
      }
    case 'failed':
      return {
        label: t('status.payment.failed.label'),
        tooltip: t('status.payment.failed.tooltip'),
        color: 'red',
        icon: React.createElement(IconXboxX),
      };
    case 'pending':
      return {
        label: t('status.payment.pending.label'),
        tooltip: t('status.payment.pending.tooltip'),
        color: 'yellow',
        icon: React.createElement(IconClock),
      };
    default:
      return {
        label: t('status.payment.failed.label'),
        tooltip: t('status.payment.failed.tooltip'),
        color: 'red',
        icon: React.createElement(IconXboxX),
      };
  }
};
